import { Component, OnInit, HostListener, ApplicationRef } from '@angular/core';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { SharedDataService } from '../../services/shared-data.service';
import { UploadService } from '../../services/uploader.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { getFormatedSize } from '../../utils/parseUtils';
import { UploadItem } from 'app/objects/uploadItem';

@Component({
  selector: 'app-uploader-dialog',
  templateUrl: './uploader-dialog.component.html',
  styleUrls: ['./uploader-dialog.component.scss'],
})


export class UploaderDialogComponent implements OnInit {
  public isUploadDisabled = false;
  public uploadButtonState = 'Start Upload';

  public START_UPLOAD = 'Start Upload';
  public RESUME_UPLOAD = 'Resume';
  public UPLOAD_QUEUED = 'Queued';
  public UPLOAD_STARTED = 'Pending...';
  public UPLOAD_UPLOADING = 'Uploading...';
  public UPLOAD_PAUSED_ON_ERROR = 'Paused on error...';
  public UPLOAD_CANCELED = 'Canceled';
  public UPLOAD_ERROR = 'Error';
  public UPLOAD_FINISHED = 'Uploaded';

  public progress = -1;
  public collection;
  public isFolderUploadAllowed = false;

  @HostListener('dragover', ['$event']) public onDragOver($event) {
    $event.preventDefault();
    $event.stopPropagation();
  }

  @HostListener('dragleave', ['$event']) public onDragLeave($event) {
    $event.preventDefault();
    $event.stopPropagation();
  }

  // Drop listener
  @HostListener('drop', ['$event']) public async ondrop($event) {
    $event.preventDefault();
    $event.stopPropagation();

    await this.getAllFileEntries($event.dataTransfer)

  }


  /* There is only one reliable method to check if it is file
      folder cannot be readed by FileReader and will throw an error.
      Relying on size 4096 is not strong solution cause also size can be 8192(on Linux distros)
      and also it can be just a file with size 4096 bytes which is fully valid
  */

  private checkIfFile(file) {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve(true);
      reader.onerror = () => resolve(false);
      reader.readAsText(file.slice(0, 1));
    });
  }
  /* Filter dataTransfer.files to remove dragged folders,
     because we can't read that folder we need to skip that folder
  */
  private async getOnlyFiles(items) {
    const files = [];
    for (let i = 0; i < items.length; i++) {
      try {
        if (await this.checkIfFile(items[i])) {
          files.push(items[i]);
        }
      } catch (error) {
        continue;
      }
    }
    return files;
  };

  // To get native File from FileEntry
  private getFile(fileEntry) {
    return new Promise((resolve, reject) => fileEntry.file(resolve, reject));
  }

  private async getAllFileEntries({ items, files }) {
    console.log(items.length, files.length)
    if (!items.length || items.length !== files.length) {
      return null;
    }

    // If browser does not support DataTransferItemList we can read only files
    if (!items[0].webkitGetAsEntry) {
      const onlyFiles = await this.getOnlyFiles(files);
      if (onlyFiles.length !== files.length) {
        this.sharedDataService.pushNotification({ text: 'Your browser does not support drag&drop of directories', type: 'warning' })
      }

      onlyFiles.forEach(file => this.uploadService.handleAddNewFile({ file, fullPath: '/' }))
      return
    }

    const fileEntries = [];
    // Use BFS to read entire directory/file structure
    const queue = [];
    for (let i = 0; i < items.length; i++) {
      try {
        queue.push((items[i].webkitGetAsEntry && items[i].webkitGetAsEntry()) || (items[i].getAsEntry && items[i].getAsEntry()));
      } catch (error) {
        console.log('File was not read', error)
      }
    }
    while (queue.length > 0) {
      const entry = queue.shift();

      if (!entry) {
        return
      }

      if (entry.isDirectory) {
        if (entry.fullPath === `/${entry.name}`) {
          const resultFromAddFolder: boolean = this.uploadService.handleAddNewFolder(entry)
          if (!resultFromAddFolder) {
            return
          }
        }
        const reader = entry.createReader();
        queue.push(...await this.readAllDirectoryEntries(reader));
      } else {
        const { fullPath } = entry;
        const file = await this.getFile(entry);
        this.uploadService.handleAddNewFile({ file, fullPath })
        this.appRef.tick();
      }
    }
    return fileEntries;
  }

  // Get all the entries (files or sub-directories) in a directory by calling readEntries until it returns empty array
  private async readAllDirectoryEntries(directoryReader) {
    const entries = [];
    let readEntries: any = await this.readEntriesPromise(directoryReader);
    while (readEntries.length > 0) {
      entries.push(...readEntries);
      readEntries = await this.readEntriesPromise(directoryReader);
    }
    return entries;
  }

  // Wrap readEntries in a promise to make working with readEntries easier
  private async readEntriesPromise(directoryReader) {
    try {
      return await new Promise((resolve, reject) => {
        directoryReader.readEntries(resolve, reject);
      });
    } catch (err) {
      console.log(err);
    }
  }

  constructor(
    public dialogRef: MatDialogRef<UploaderDialogComponent>,
    public sharedDataService: SharedDataService,
    public confirmationDialog: MatDialog,
    public uploadService: UploadService,
    private appRef: ApplicationRef,
  ) {
    this.sharedDataService.closeUploaderObservable.subscribe(shouldCloseDialog => {
      if (shouldCloseDialog) {
        this.onNoClick(false)
      }
    })
    this.uploadService.startButtonDisabledObservable.subscribe(value => {
      this.setIsUploadDisabled(value)
    })
    this.uploadService.startButtonStatusObservable.subscribe(value => {
      this.setUploadButtonState(value)
    })

    this.collection = this.uploadService.uploadCollection;

    this.sharedDataService.uploadProgressObservable.subscribe({
      next: value => {
        this.progress = value;
      },
    });
  }

  get isEmpty() {
    return this.collection ? !this.collection.items.length : true;
  }

  get isActiveUpload() {
    return this.progress > -1 && this.progress < 100;
  }

  ngOnInit() {
    this.isFolderUploadAllowed = window['support'].directoryUpload;
  }


  public handleDeleteAll(): void {
    this.uploadService.handleDeleteAll();
  }
  public handleDeleteItem(file: UploadItem): void {
    if (this.collection.items.length === 1 && this.isActiveUpload) {
      this.onNoClick(true)
    } else {
      this.uploadService.handleRemoveItem(file, this.UPLOAD_CANCELED);
    }
  }

  public getSize(value: number): string {
    return getFormatedSize(value)
  }

  public handleFileInput(event): void {
    this.uploadService.handleFilesFromInput(event.target.files)
  }

  public handleUploadStart(): void {
    this.uploadService.handleStartButton();
    this.setIsUploadDisabled(true);
  }

  public setUploadButtonState(value: string): void {
    this.uploadButtonState = value;
  }

  public setIsUploadDisabled(disabled: boolean): void {
    this.isUploadDisabled = disabled;
  }

  public getSpeedWithProgress(item): string {
    return `${item.speed}(${Math.round((100 * item.received) / item.size)}%)`;
  }

  onNoClick(isClose: boolean): void {
    if ((this.isActiveUpload || this.collection.items.some(item => this.UPLOAD_PAUSED_ON_ERROR === item.state)) && isClose) {
      const dialogRef = this.confirmationDialog.open(ConfirmationDialogComponent, {
        width: '22vw',
        height: '18vh',
        autoFocus: false,
        panelClass: 'common-dialog',
        data: {
          title: 'Cancel Upload',
          message: 'Are you sure want to cancel upload?',
          cancelLabel: 'NO',
          actionLabel: 'YES'
        }
      });
      dialogRef.afterClosed()
        .subscribe((result) => {
          if (result && result.isConfirmed) {
            this.handleDeleteAll();
            this.dialogRef.close()
            this.sharedDataService.pushNotification({ text: 'The upload was canceled', type: 'info' })
          }
        })
    } else {
      if (!this.isActiveUpload
        && this.collection.items.some(item => [this.UPLOAD_PAUSED_ON_ERROR, this.UPLOAD_ERROR].includes(item.state))) {
        this.handleDeleteAll();
      }
      this.dialogRef.close()
    }
  }
}
